import React from 'react';
import {
  AnimalActivityPayload,
  AutomobileLossClaimEventPayload,
  DriverActivityPayload,
  EndorsementActivityPayload,
  EquipmentActivityPayload,
  GeneralLiabilityClaimEventPayload,
  GroupHealthMemberActivityPayload,
  HoaUnitActivityPayload,
  ImportantDateActivityPayload,
  LifeInsuranceActivityPayload,
  MasterCertificateActivityPayload,
  MotorTruckCargoActivityPayload,
  NoteEventPayload,
  OtherClaimEventPayload,
  PolicyEventPayload,
  ProfessionalLiabilityActivityPayload,
  PropertyActivityPayload,
  PropertyLossClaimEventPayload,
  ServiceRequestAddDriverEventPayload,
  ServiceRequestAddressChangeEventPayload,
  ServiceRequestGeneralNewEventPayload,
  ServiceRequestGenericEventPayload,
  ServiceRequestRemoveDriverEventPayload,
  ServiceRequestReplaceDriverEventPayload,
  ServiceRequestVehicleTransferEventPayload,
  TagApplyEventPayload,
  TagApplyOnPolicyEventPayload,
  TaskEventPayload,
  TodoEventPayload,
  TruckingCompanyEventPayload,
  TruckingCompanyOpportunityEventPayload,
  UserActivityEvent,
  VehicleActivityPayload,
  WatercraftActivityPayload,
  WorkerCompensationClaimEventPayload,
  XDateEventPayload,
} from './models';
import moment from 'moment';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { Card, Spin } from 'antd';
import { VendorPortalEvents } from '../../shared/constants/enums';

type UserActivityEventItemProps = {
  userActivityEvent: UserActivityEvent;
};

const UserActivityEventItem: React.FC<UserActivityEventItemProps> = (props) => {
  const { humanReadableEventTypes, humanReadableEventTypesLoading } = useSelector((state: RootState) => state.userActivityEvents);

  const eventDescription = (): React.ReactNode => {
    let description = <></>;
    switch (props.userActivityEvent.eventType) {
      case VendorPortalEvents.POLICY_DELETE:
      case VendorPortalEvents.POLICY_INSERT:
      case VendorPortalEvents.POLICY_UPDATE:
      case VendorPortalEvents.QUOTE_DELETE:
      case VendorPortalEvents.QUOTE_INSERT:
      case VendorPortalEvents.QUOTE_UPDATE:
      case VendorPortalEvents.POLICY_STATUS_CHANGED:
        let policyEventPayload = props.userActivityEvent.payloadAfter as PolicyEventPayload;
        description = (
          <div>
            {policyEventPayload.number}
            <br />
            Changed by {policyEventPayload.lastChangeUserName}
          </div>
        );
        break;

      case VendorPortalEvents.INSURED_DELETE:
      case VendorPortalEvents.INSURED_INSERT:
      case VendorPortalEvents.INSURED_UPDATE:
      case VendorPortalEvents.PROSPECT_DELETE:
      case VendorPortalEvents.PROSPECT_INSERT:
      case VendorPortalEvents.PROSPECT_UPDATE:
      case VendorPortalEvents.REFERRAL_DELETE:
      case VendorPortalEvents.REFERRAL_INSERT:
      case VendorPortalEvents.REFERRAL_UPDATE:
      case VendorPortalEvents.MGA_DELETE:
      case VendorPortalEvents.MGA_INSERT:
      case VendorPortalEvents.MGA_UPDATE:
      case VendorPortalEvents.CARRIER_DELETE:
      case VendorPortalEvents.CARRIER_INSERT:
      case VendorPortalEvents.CARRIER_UPDATE:
      case VendorPortalEvents.FINANCECOMPANY_DELETE:
      case VendorPortalEvents.FINANCECOMPANY_INSERT:
      case VendorPortalEvents.FINANCECOMPANY_UPDATE:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_DELETE:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_INSERT:
      case VendorPortalEvents.OTHERTRUCKINGCOMPANY_UPDATE:
        let insuredEventPayload = props.userActivityEvent.payloadAfter as TruckingCompanyEventPayload;
        description = <div>{insuredEventPayload.commercialName}</div>;
        break;

      case VendorPortalEvents.NOTE_DELETE:
      case VendorPortalEvents.NOTE_INSERT:
      case VendorPortalEvents.NOTE_UPDATE:
        let noteEventPayload = props.userActivityEvent.payloadAfter as NoteEventPayload;
        description = (
          <div>
            Creator: {noteEventPayload.creatorName}
            <br />
            {noteEventPayload.subject.length > 100 ? noteEventPayload.subject.slice(0, 100) + '...' : noteEventPayload.subject}
          </div>
        );
        break;

      case VendorPortalEvents.TASK_DELETE:
      case VendorPortalEvents.TASK_INSERT:
      case VendorPortalEvents.TASK_UPDATE:
        let taskEventPayload = props.userActivityEvent.payloadAfter as TaskEventPayload;
        description = (
          <div>
            Creator: {taskEventPayload.creatorName}
            <br />
            {taskEventPayload.title.length > 100 ? taskEventPayload.title.slice(0, 100) + '...' : taskEventPayload.title}
          </div>
        );
        break;

      case VendorPortalEvents.TODO_DELETE:
      case VendorPortalEvents.TODO_INSERT:
      case VendorPortalEvents.TODO_UPDATE:
        let todoEventPayload = props.userActivityEvent.payloadAfter as TodoEventPayload;
        description = <div>{todoEventPayload.title.length > 100 ? todoEventPayload.title.slice(0, 100) + '...' : todoEventPayload.title}</div>;
        break;

      case VendorPortalEvents.DRIVER_DELETE:
      case VendorPortalEvents.DRIVER_INSERT:
      case VendorPortalEvents.DRIVER_UPDATE:
        let driverEventPayload = props.userActivityEvent.payloadAfter as DriverActivityPayload;
        description = <div>{[driverEventPayload.firstName, driverEventPayload.lastName].filter(Boolean).join(' ')}</div>;
        break;

      case VendorPortalEvents.VEHICLE_DELETE:
      case VendorPortalEvents.VEHICLE_INSERT:
      case VendorPortalEvents.VEHICLE_UPDATE:
        let vehicleEventPayload = props.userActivityEvent.payloadAfter as VehicleActivityPayload;
        description = (
          <div>
            {[vehicleEventPayload.make, vehicleEventPayload.model, vehicleEventPayload.year, vehicleEventPayload.vIN].filter(Boolean).join(' ')}
          </div>
        );
        break;

      case VendorPortalEvents.PROPERTY_DELETE:
      case VendorPortalEvents.PROPERTY_INSERT:
      case VendorPortalEvents.PROPERTY_UPDATE:
        let propertyEventPayload = props.userActivityEvent.payloadAfter as PropertyActivityPayload;
        description = (
          <div>
            {[
              propertyEventPayload.locationNumber,
              propertyEventPayload.addressLine1,
              propertyEventPayload.addressLine2,
              propertyEventPayload.city,
              propertyEventPayload.state,
              propertyEventPayload.zip,
            ]
              .filter(Boolean)
              .join(',')}
          </div>
        );
        break;

      case VendorPortalEvents.EQUIPMENT_DELETE:
      case VendorPortalEvents.EQUIPMENT_INSERT:
      case VendorPortalEvents.EQUIPMENT_UPDATE:
        let equipmentEventPayload = props.userActivityEvent.payloadAfter as EquipmentActivityPayload;
        description = <div>{[equipmentEventPayload.unitNumber, equipmentEventPayload.idSerialNumber].filter(Boolean).join(', ')}</div>;
        break;

      case VendorPortalEvents.PROFESSIONAL_LIABILITY_DELETE:
      case VendorPortalEvents.PROFESSIONAL_LIABILITY_INSERT:
      case VendorPortalEvents.PROFESSIONAL_LIABILITY_UPDATE:
        let professionalLiabilityEventPayload = props.userActivityEvent.payloadAfter as ProfessionalLiabilityActivityPayload;
        description = (
          <div>
            {[professionalLiabilityEventPayload.firstName, professionalLiabilityEventPayload.lastName, professionalLiabilityEventPayload.title]
              .filter(Boolean)
              .join(' ')}
          </div>
        );
        break;

      case VendorPortalEvents.MOTOR_TRUCK_CARGO_DELETE:
      case VendorPortalEvents.MOTOR_TRUCK_CARGO_INSERT:
      case VendorPortalEvents.MOTOR_TRUCK_CARGO_UPDATE:
        let motorTruckCargoEventPayload = props.userActivityEvent.payloadAfter as MotorTruckCargoActivityPayload;
        description = <div>{motorTruckCargoEventPayload.commoditiesHauled}</div>;
        break;

      case VendorPortalEvents.HOA_UNIT_DELETE:
      case VendorPortalEvents.HOA_UNIT_INSERT:
      case VendorPortalEvents.HOA_UNIT_UPDATE:
        let hoaUnitEventPayload = props.userActivityEvent.payloadAfter as HoaUnitActivityPayload;
        description = (
          <div>{[hoaUnitEventPayload.unitNumber, hoaUnitEventPayload.ownerVesting, hoaUnitEventPayload.loanNumber].filter(Boolean).join(', ')}</div>
        );
        break;

      case VendorPortalEvents.GROUP_HEALTH_MEMBER_DELETE:
      case VendorPortalEvents.GROUP_HEALTH_MEMBER_INSERT:
      case VendorPortalEvents.GROUP_HEALTH_MEMBER_UPDATE:
        let groupHealthMemberEventPayload = props.userActivityEvent.payloadAfter as GroupHealthMemberActivityPayload;
        description = (
          <div>
            {[groupHealthMemberEventPayload.firstName, groupHealthMemberEventPayload.lastName, groupHealthMemberEventPayload.citizenship]
              .filter(Boolean)
              .join(' ')}
          </div>
        );
        break;

      case VendorPortalEvents.WATERCRAFT_DELETE:
      case VendorPortalEvents.WATERCRAFT_INSERT:
      case VendorPortalEvents.WATERCRAFT_UPDATE:
        let watercraftEventPayload = props.userActivityEvent.payloadAfter as WatercraftActivityPayload;
        description = (
          <div>
            {watercraftEventPayload.model} {watercraftEventPayload.modelYear}
          </div>
        );
        break;

      case VendorPortalEvents.LIFE_INS_DELETE:
      case VendorPortalEvents.LIFE_INS_INSERT:
      case VendorPortalEvents.LIFE_INS_UPDATE:
        let lifeInsuranceEventPayload = props.userActivityEvent.payloadAfter as LifeInsuranceActivityPayload;
        description = (
          <div>
            {[lifeInsuranceEventPayload.firstName, lifeInsuranceEventPayload.lastName, lifeInsuranceEventPayload.type].filter(Boolean).join(' ')}
          </div>
        );
        break;

      case VendorPortalEvents.ANIMAL_DELETE:
      case VendorPortalEvents.ANIMAL_INSERT:
      case VendorPortalEvents.ANIMAL_UPDATE:
        let animalEventPayload = props.userActivityEvent.payloadAfter as AnimalActivityPayload;
        description = <div>{[animalEventPayload.name, animalEventPayload.sire, animalEventPayload.dam].filter(Boolean).join(', ')}</div>;
        break;

      case VendorPortalEvents.IMPORTANT_DATE_DELETE:
      case VendorPortalEvents.IMPORTANT_DATE_INSERT:
      case VendorPortalEvents.IMPORTANT_DATE_UPDATE:
        let importantDateEventPayload = props.userActivityEvent.payloadAfter as ImportantDateActivityPayload;
        description = (
          <div>
            Value: {importantDateEventPayload.importantDate ? moment(importantDateEventPayload.importantDate).format('MM/DD/YYYY h:mm:ss A') : ''}
          </div>
        );
        break;

      case VendorPortalEvents.ENDORSEMENT_DELETE:
      case VendorPortalEvents.ENDORSEMENT_INSERT:
      case VendorPortalEvents.ENDORSEMENT_UPDATE:
        let endorsementEventPayload = props.userActivityEvent.payloadAfter as EndorsementActivityPayload;
        description = <div>{[endorsementEventPayload.amount, endorsementEventPayload.description].filter(Boolean).join(', ')}</div>;
        break;

      case VendorPortalEvents.MASTERCERTIFICATE_DELETE:
      case VendorPortalEvents.MASTERCERTIFICATE_INSERT:
      case VendorPortalEvents.MASTERCERTIFICATE_UPDATE:
        let masterCertificateEventPayload = props.userActivityEvent.payloadAfter as MasterCertificateActivityPayload;
        description = (
          <div>
            {masterCertificateEventPayload.name}
            <br />
            Changed by {masterCertificateEventPayload.lastChangedUserName}
          </div>
        );
        break;

      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_DELETE:
      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_INSERT:
      case VendorPortalEvents.TRUCKINGCOMPANYOPPORTUNITY_UPDATE:
        let truckingCompanyOpportunityEventPayload = props.userActivityEvent.payloadAfter as TruckingCompanyOpportunityEventPayload;
        description = <div>{truckingCompanyOpportunityEventPayload.lineOfBusinessName}</div>;
        break;

      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTADDDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_DELETE:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTADDRESSCHANGE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTREMOVEDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTREPLACEDRIVER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_DELETE:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_INSERT:
      case VendorPortalEvents.SERVICEREQUESTVEHICLETRANSFER_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_DELETE:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_INSERT:
      case VendorPortalEvents.SERVICEREQUESTGENERALNEW_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTGENERIC_DELETE:
      case VendorPortalEvents.SERVICEREQUESTGENERIC_INSERT:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_UPDATE:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_INSERT:
      case VendorPortalEvents.SERVICEREQUESTPOLICYCHANGE_DELETE:
        let serviceRequestAddDriverEventPayload = props.userActivityEvent.payloadAfter as
          | ServiceRequestAddDriverEventPayload
          | ServiceRequestAddressChangeEventPayload
          | ServiceRequestRemoveDriverEventPayload
          | ServiceRequestReplaceDriverEventPayload
          | ServiceRequestVehicleTransferEventPayload
          | ServiceRequestGeneralNewEventPayload
          | ServiceRequestGenericEventPayload;
        description = <div>{serviceRequestAddDriverEventPayload.description}</div>;
        break;

      case VendorPortalEvents.OTHERCLAIM_DELETE:
      case VendorPortalEvents.OTHERCLAIM_INSERT:
      case VendorPortalEvents.OTHERCLAIM_UPDATE:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_DELETE:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_INSERT:
      case VendorPortalEvents.AUTOMOBILELOSSCLAIM_UPDATE:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_DELETE:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_INSERT:
      case VendorPortalEvents.GENERALLIABILITYCLAIM_UPDATE:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_DELETE:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_INSERT:
      case VendorPortalEvents.PROPERTYLOSSCLAIM_UPDATE:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_DELETE:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_INSERT:
      case VendorPortalEvents.WORKERCOMPENSATIONCLAIM_UPDATE:
        let otherClaimEventPayload = props.userActivityEvent.payloadAfter as
          | OtherClaimEventPayload
          | AutomobileLossClaimEventPayload
          | GeneralLiabilityClaimEventPayload
          | PropertyLossClaimEventPayload
          | WorkerCompensationClaimEventPayload;
        description = <div>{otherClaimEventPayload.claimNumber}</div>;
        break;

      case VendorPortalEvents.TAGAPPLY_DELETE:
      case VendorPortalEvents.TAGAPPLY_INSERT:
      case VendorPortalEvents.TAGAPPLY_UPDATE:
      case VendorPortalEvents.TAGAPPLYONPOLICY_DELETE:
      case VendorPortalEvents.TAGAPPLYONPOLICY_INSERT:
      case VendorPortalEvents.TAGAPPLYONPOLICY_UPDATE:
        let tagApplyEventPayload = props.userActivityEvent.payloadAfter as TagApplyEventPayload | TagApplyOnPolicyEventPayload;
        description = (
          <div>
            {tagApplyEventPayload.tagName} {tagApplyEventPayload.tagDescription}
          </div>
        );
        break;
      case VendorPortalEvents.XDate_DELETE:
      case VendorPortalEvents.XDate_INSERT:
      case VendorPortalEvents.XDate_UPDATE:
        let xDateEventPayload = props.userActivityEvent.payloadAfter as XDateEventPayload;
        description = (
          <div>
            X-Date: {xDateEventPayload.xDate ? moment(xDateEventPayload.xDate).format('MM/DD/YYYY h:mm:ss A') : ''}
            <br />
            {[xDateEventPayload.insuredFirstName, xDateEventPayload.insuredLastName].filter(Boolean).join(' ')}
          </div>
        );
        break;

      default:
        description = <div style={{ color: 'red' }}>NOT IMPLEMENTED!</div>;
        break;
    }

    return description;
  };

  return (
    <div className="user-activity-event-item">
      <Spin spinning={humanReadableEventTypesLoading} tip="Loading...">
        <Card className="user-activity-event-card" size="small">
          <b>
            {humanReadableEventTypes &&
              Array.isArray(humanReadableEventTypes) &&
              humanReadableEventTypes.find((item: { key: string; value: string }) => item.key === props.userActivityEvent.eventType)?.value}
          </b>
          <br />
          {moment(props.userActivityEvent.eventDateUtc).utc(false).format('MM/DD/YYYY h:mm:ss A')}
          <br />
          <div title={props.userActivityEvent.objectId}>{eventDescription()}</div>
        </Card>
      </Spin>
    </div>
  );
};

export default UserActivityEventItem;
